import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { STATUS_OPTIONS, getStatusLabel } from '../../utils/eventStatusMapping';
import {
  formatSlug,
  validateSlugFormat,
  checkSlugAvailability,
  uploadEventFeaturedImage,
} from '../../services/eventsService';
import { getBaseUrlForEvent } from '../../utils/sharingLinkUrl';
import { getEventLocationFieldLabels } from '../../utils/eventLocation';
import {
  BUCKET_EVENT_IMAGES,
  getPublicImageUrl,
  IMAGE_UPLOAD_ACCEPT,
  validateImageFile,
} from '../../utils/storage';

const { FiImage, FiUploadCloud } = FiIcons;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(value) {
  return typeof value === 'string' && value.trim().length > 0 && EMAIL_REGEX.test(value.trim());
}

function resolveFeaturedImagePreview(event) {
  const raw = event?.featured_image_url || event?.image_url || '';
  if (!raw) return null;
  return getPublicImageUrl(raw, BUCKET_EVENT_IMAGES) || raw;
}

export default function BasicInfoTab({ event, onChange, stripeOnboardingCompleted }) {
  const [slugError, setSlugError] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [newNotificationEmail, setNewNotificationEmail] = useState('');
  const [notificationEmailError, setNotificationEmailError] = useState(null);
  const [uploadingFeaturedImage, setUploadingFeaturedImage] = useState(false);
  const [dragOverFeaturedImage, setDragOverFeaturedImage] = useState(false);
  const featuredImageInputRef = useRef(null);
  const isPublished = event?.status === 'public';
  const currentSlug = event?.event_slug || '';
  const displaySlug = (slugTouched ? (event?.event_slug ?? '') : currentSlug) || event?.event_slug || '';
  const locationLabels = getEventLocationFieldLabels(event?.format || 'online');

  const handleSlugChange = (e) => {
    const value = e.target.value;
    onChange({ event_slug: value });
    setSlugError(null);
  };

  const handleSlugBlur = async () => {
    setSlugTouched(true);
    const slug = formatSlug(event?.event_slug) || String(event?.event_slug || '').trim();
    if (!slug) {
      setSlugError(null);
      return;
    }
    const formatCheck = validateSlugFormat(slug);
    if (!formatCheck.valid) {
      setSlugError(formatCheck.message);
      return;
    }
    const { data } = await checkSlugAvailability(slug, event?.id);
    setSlugError(data && !data.available ? 'This slug is already in use. Please choose another.' : null);
  };

  async function handleFeaturedImageFile(file) {
    if (!file || !event?.id) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.message ?? 'Invalid image file');
      return;
    }

    setUploadingFeaturedImage(true);
    try {
      const { data, error } = await uploadEventFeaturedImage(event.id, file);
      if (error) {
        toast.error(error.message ?? 'Upload failed');
        return;
      }
      const publicUrl = data?.publicUrl || getPublicImageUrl(data?.path, BUCKET_EVENT_IMAGES);
      if (!publicUrl) {
        toast.error('Upload succeeded but could not resolve public URL');
        return;
      }
      onChange({ featured_image_url: publicUrl, image_url: publicUrl });
      toast.success('Featured image uploaded');
    } finally {
      setUploadingFeaturedImage(false);
    }
  }

  async function handleFeaturedImageInputChange(e) {
    const file = e.target.files?.[0];
    await handleFeaturedImageFile(file);
    e.target.value = '';
  }

  function handleFeaturedImageDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFeaturedImage(true);
  }

  function handleFeaturedImageDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFeaturedImage(false);
  }

  async function handleFeaturedImageDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFeaturedImage(false);
    const file = e.dataTransfer?.files?.[0];
    await handleFeaturedImageFile(file);
  }

  const featuredImagePreview = resolveFeaturedImagePreview(event);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Event Title */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Title *
            </label>
            <input
              type="text"
              value={event?.title || ''}
              onChange={(e) => onChange({ title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter event title"
            />
          </div>

          {/* Event Slug */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Slug *
            </label>
            <input
              type="text"
              value={event?.event_slug ?? ''}
              onChange={handleSlugChange}
              onBlur={handleSlugBlur}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent lowercase"
              placeholder="my-event-slug"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be part of your event URL. Use lowercase letters, numbers, and hyphens only.
            </p>
            {(event?.event_slug ?? '') && (
              <p className="text-xs text-gray-600 mt-1 font-mono">
                {getBaseUrlForEvent(event)}/events/{formatSlug(event?.event_slug) || event?.event_slug?.trim() || '…'}
              </p>
            )}
            {isPublished && (
              <p className="text-amber-700 text-xs mt-1">
                Changing the slug will break existing links. Consider redirects if you change it.
              </p>
            )}
            {slugError && (
              <p className="text-red-500 text-sm mt-1">{slugError}</p>
            )}
          </div>

          {/* Subtitle */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subtitle
            </label>
            <input
              type="text"
              value={event?.subtitle || ''}
              onChange={(e) => onChange({ subtitle: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Compelling subtitle for your event"
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={event?.description || ''}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe your event..."
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Type *
            </label>
            <select
              value={event?.type || 'seminar'}
              onChange={(e) => onChange({ type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="seminar">Seminar</option>
              <option value="workshop">Workshop</option>
              <option value="course">Course</option>
              <option value="webinar">Webinar</option>
              <option value="conference">Conference</option>
            </select>
          </div>

          {/* Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Format
            </label>
            <select
              value={event?.format || 'online'}
              onChange={(e) => onChange({ format: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="online">Online</option>
              <option value="in_person">In-Person</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={event?.status || 'draft'}
              onChange={(e) => onChange({ status: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.dbValue} value={option.dbValue}>
                  {option.label}
                </option>
              ))}
            </select>
            {(event?.price ?? 0) > 0 && (event?.status || 'draft') === 'public' && !event?.use_platform_account && stripeOnboardingCompleted === false && (
              <p className="mt-1 text-xs text-amber-700">
                You must connect your Stripe account before publishing paid events.
              </p>
            )}
          </div>

          {/* Capacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Attendees
            </label>
            <input
              type="number"
              value={event?.capacity || event?.max_attendees || ''}
              onChange={(e) => onChange({ capacity: parseInt(e.target.value) || null, max_attendees: parseInt(e.target.value) || null })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Unlimited"
              min="0"
            />
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Language
            </label>
            <select
              value={event?.language === 'en' ? 'en' : 'es'}
              onChange={(e) => onChange({ language: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="es">Spanish</option>
              <option value="en">English</option>
            </select>
          </div>

          {/* Location name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {locationLabels.nameLabel}
            </label>
            <input
              type="text"
              value={event?.location_name ?? event?.location ?? ''}
              onChange={(e) => onChange({ location_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={locationLabels.namePlaceholder}
            />
          </div>

          {/* Location address / meeting URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {locationLabels.addressLabel}
            </label>
            <input
              type="text"
              value={event?.location_address ?? ''}
              onChange={(e) => onChange({ location_address: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={locationLabels.addressPlaceholder}
            />
          </div>

          {/* Custom Domain (optional) */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Domain (optional)
            </label>
            <input
              type="text"
              value={event?.custom_domain ?? ''}
              onChange={(e) => onChange({ custom_domain: e.target.value.trim() || null })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g. eventos.mujeresyriquezas.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use a custom domain for invitation links. Leave blank to use the default domain.
            </p>
          </div>

          {/* Target Audience */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Audience
            </label>
            <input
              type="text"
              value={event?.target_audience || ''}
              onChange={(e) => onChange({ target_audience: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Beginner investors, Financial advisors"
            />
          </div>

          {/* Prerequisites */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prerequisites
            </label>
            <textarea
              value={event?.prerequisites || ''}
              onChange={(e) => onChange({ prerequisites: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Any requirements or prerequisites for attendees"
            />
          </div>

          {/* Checkboxes */}
          <div className="md:col-span-2 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={event?.certificate_available || false}
                onChange={(e) => onChange({ certificate_available: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Certificate of completion available</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={event?.recording_available || false}
                onChange={(e) => onChange({ recording_available: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Recording will be available</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={event?.countdown_enabled || false}
                onChange={(e) => onChange({ countdown_enabled: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Enable countdown timer on landing page</span>
            </label>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price ($)
            </label>
            <input
              type="number"
              value={event?.price || 0}
              onChange={(e) => onChange({ price: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0 for free events"
              min="0"
              step="0.01"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Early Bird Price ($)
            </label>
            <input
              type="number"
              value={event?.early_bird_price || ''}
              onChange={(e) => onChange({ early_bird_price: parseFloat(e.target.value) || null })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Optional early bird price"
              min="0"
              step="0.01"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Early Bird Deadline
            </label>
            <input
              type="datetime-local"
              value={event?.early_bird_deadline ? event.early_bird_deadline.slice(0, 16) : ''}
              onChange={(e) => onChange({ early_bird_deadline: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Use Platform Payment Account */}
          <div className="md:col-span-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={event?.use_platform_account || false}
                onChange={(e) => onChange({ use_platform_account: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Use Platform Payment Account</span>
            </label>
            <p className="mt-1 text-xs text-gray-500">
              Enable this to receive payments in the main Prosperity Leaders account instead of your connected Stripe account.
            </p>
          </div>
        </div>
      </div>

      {/* Free Orientation Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Free Orientation</h3>
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={event?.has_free_orientation || false}
              onChange={(e) => {
                const checked = e.target.checked;
                onChange({
                  has_free_orientation: checked,
                  ...(checked ? {} : { orientation_session_id: null })
                });
              }}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">Has Free Orientation</span>
          </label>
          <p className="text-xs text-gray-500">
            When enabled, users who complete Step 1 (name, email, phone) are automatically registered for the orientation session and can attend without completing payment or account creation. They can return later to complete full registration.
          </p>
          {event?.has_free_orientation && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Orientation Session *
              </label>
              <select
                value={event?.orientation_session_id || ''}
                onChange={(e) => onChange({ orientation_session_id: e.target.value || null })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a session...</option>
                {(event?.sessions || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.session_title || s.title || `Session ${s.session_number ?? s.id?.slice(0, 8)}`}
                  </option>
                ))}
              </select>
              {(!event?.sessions || event.sessions.length === 0) && (
                <p className="mt-1 text-xs text-amber-600">
                  Add sessions in the <strong>Sessions</strong> tab first, then choose one as the orientation session.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Registration Notifications */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Notifications</h3>
        <p className="text-sm text-gray-600 mb-4">
          Email addresses listed here will be notified when a new registration is completed for this event.
        </p>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {(event?.notification_emails || []).map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-800 text-sm"
              >
                {email}
                <button
                  type="button"
                  onClick={() => {
                    const current = event?.notification_emails || [];
                    onChange({
                      notification_emails: current.filter((e) => e !== email)
                    });
                  }}
                  className="text-gray-500 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded p-0.5"
                  aria-label={`Remove ${email}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap items-start">
            <div className="flex-1 min-w-[200px]">
              <input
                type="email"
                value={newNotificationEmail}
                onChange={(e) => {
                  setNewNotificationEmail(e.target.value);
                  setNotificationEmailError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const trimmed = newNotificationEmail.trim();
                    if (!trimmed) return;
                    if (!isValidEmail(trimmed)) {
                      setNotificationEmailError('Please enter a valid email address.');
                      return;
                    }
                    const current = event?.notification_emails || [];
                    if (current.includes(trimmed)) {
                      setNotificationEmailError('This email is already in the list.');
                      return;
                    }
                    onChange({ notification_emails: [...current, trimmed] });
                    setNewNotificationEmail('');
                    setNotificationEmailError(null);
                  }
                }}
                placeholder="Add email address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {notificationEmailError && (
                <p className="text-red-500 text-sm mt-1">{notificationEmailError}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                const trimmed = newNotificationEmail.trim();
                if (!trimmed) {
                  setNotificationEmailError('Please enter an email address.');
                  return;
                }
                if (!isValidEmail(trimmed)) {
                  setNotificationEmailError('Please enter a valid email address.');
                  return;
                }
                const current = event?.notification_emails || [];
                if (current.includes(trimmed)) {
                  setNotificationEmailError('This email is already in the list.');
                  return;
                }
                onChange({ notification_emails: [...current, trimmed] });
                setNewNotificationEmail('');
                setNotificationEmailError(null);
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Date & Time Section - Note: Event timing is managed via Sessions tab */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date & Time</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Event dates and times are managed through the <strong>Sessions</strong> tab.
            Add one or more sessions to set specific start and end times for your event.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <select
              value={event?.timezone || 'America/New_York'}
              onChange={(e) => onChange({ timezone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>
      </div>

      {/* Media Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Media</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image
            </label>
            <div
              role="button"
              tabIndex={0}
              onClick={() => !uploadingFeaturedImage && featuredImageInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!uploadingFeaturedImage) featuredImageInputRef.current?.click();
                }
              }}
              onDragOver={handleFeaturedImageDragOver}
              onDragLeave={handleFeaturedImageDragLeave}
              onDrop={handleFeaturedImageDrop}
              className={`mb-3 flex items-center gap-4 rounded-lg border-2 border-dashed p-4 transition-colors cursor-pointer ${
                dragOverFeaturedImage
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 bg-gray-50'
              } ${uploadingFeaturedImage ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <div className="w-24 h-16 rounded-lg bg-white border flex items-center justify-center overflow-hidden flex-shrink-0">
                {featuredImagePreview ? (
                  <img
                    src={featuredImagePreview}
                    alt="Featured preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <SafeIcon icon={FiImage} className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                  <SafeIcon icon={FiUploadCloud} className="w-4 h-4 text-blue-600" />
                  {uploadingFeaturedImage ? 'Uploading…' : 'Click or drag an image to upload'}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG, or WebP · max 5MB · stored in event-images
                </p>
              </div>
              <input
                ref={featuredImageInputRef}
                type="file"
                accept={IMAGE_UPLOAD_ACCEPT}
                className="hidden"
                onChange={handleFeaturedImageInputChange}
              />
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image URL <span className="font-normal text-gray-500">(manual override)</span>
            </label>
            <input
              type="url"
              value={event?.featured_image_url || event?.image_url || ''}
              onChange={(e) => onChange({ featured_image_url: e.target.value, image_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com/image.jpg"
            />
            {featuredImagePreview && (
              <div className="mt-2">
                <img
                  src={featuredImagePreview}
                  alt="Event preview"
                  className="w-48 h-32 object-cover rounded-lg border"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video URL (YouTube, Vimeo, etc.)
            </label>
            <input
              type="url"
              value={event?.video_url || ''}
              onChange={(e) => onChange({ video_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
